import { Component, ChangeDetectionStrategy, inject, input, signal, computed, effect, DestroyRef } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";

import { FloatLabelModule } from "primeng/floatlabel";
import { SelectModule } from "primeng/select";
import { ButtonModule } from "primeng/button";
import { EMPTY, Observable, Subject, catchError, distinctUntilChanged, merge, of, switchMap, tap } from "rxjs";

import { AttioClient, AttioDealOption, SalesPerson } from "src/api-service";
import { normalizeError } from "src/app/store/app-store-error.types";
import { CoreErrorsComponent } from "src/app/shared/components/core-errors/core-errors.component";
import { AttioWorkspaceService } from "src/app/shared/services/attio-workspace.service";

/** A deal as offered in the list; `disabled` when a customer is required and the deal has none. */
type DealChoice = AttioDealOption & { disabled: boolean };

/** The longest opportunity name a quote takes. */
export const OPPORTUNITY_NAME_MAX_LENGTH = 70;

/** A quote's opportunity name starts as its deal's name, cut to what the quote allows. */
export function dealOpportunityName(deal: AttioDealOption): string {
    return (deal.name ?? "").slice(0, OPPORTUNITY_NAME_MAX_LENGTH);
}

/**
 * Picks one of a salesperson's open Attio deals, which every quote created in Core is linked to.
 * Loads the deals itself, since the list is only needed while the picker is shown, and again whenever the
 * salesperson changes. A deal picked for another salesperson is cleared, so it can't be sent with the wrong one.
 */
@Component({
    selector: "app-select-attio-deal",
    templateUrl: "./select-attio-deal.component.html",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, TranslateModule, FloatLabelModule, SelectModule, ButtonModule, CoreErrorsComponent],
})
export class SelectAttioDealComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly attioClient = inject(AttioClient);
    private readonly destroyRef = inject(DestroyRef);
    private readonly attioWorkspace = inject(AttioWorkspaceService).workspace;

    // =========================================================================
    // INPUTS
    // =========================================================================
    /** The form control holding the picked deal. */
    public readonly control = input.required<FormControl<AttioDealOption | null>>();
    /** Only deals whose company has a Core customer code can be picked (the deal decides the customer). */
    public readonly requireCustomer = input(false);
    /** A deal to pick once the list has loaded, e.g. the duplicated quote's deal. */
    public readonly preselectRecordId = input<string | null | undefined>(null);
    /** Whose open deals are offered; none are until one is picked. */
    public readonly salesPerson = input<SalesPerson | null | undefined>(null);

    // =========================================================================
    // LOCAL STATE
    // =========================================================================
    private readonly deals = signal<AttioDealOption[]>([]);
    public readonly isLoading = signal(false);
    public readonly errorMessage = signal<string | null>(null);
    private readonly retry$ = new Subject<void>();


    // =========================================================================
    // COMPUTED
    // =========================================================================
    /** Compared by id, so a reloaded list of sales persons doesn't reload the deals. */
    private readonly salesPersonId = computed(() => this.salesPerson()?.id ?? null);

    /** Deals can only be refreshed for a salesperson. */
    public readonly hasSalesPerson = computed(() => this.salesPersonId() !== null);

    /** The deals in the environment's Attio workspace; null until it is known. */
    public readonly attioDealsUrl = computed(() => this.attioWorkspace()?.dealsUrl ?? null);

    /** The salesperson's deals have loaded and none are open: the list links to Attio, where deals are made. */
    public readonly hasNoOpenDeals = computed(() => this.salesPersonId() !== null && !this.isLoading() && !this.errorMessage());

    public readonly emptyMessageKey = computed(() => {
        if (this.salesPersonId() === null) return "ATTIO_DEAL_PICKER.SELECT_SALESPERSON_FIRST";
        return this.isLoading() ? "COMMON.LOADING" : "ATTIO_DEAL_PICKER.NO_OPEN_DEALS";
    });

    public readonly choices = computed<DealChoice[]>(() =>
        this.deals().map(deal => ({ ...deal, disabled: this.requireCustomer() && !deal.customerCode })),
    );

    // =========================================================================
    // EFFECTS
    // =========================================================================
    private readonly preselectEffect = effect(() => {
        const recordId = this.preselectRecordId();
        const choice = recordId ? this.choices().find(deal => deal.recordId === recordId && !deal.disabled) : undefined;
        if (choice && !this.control().value) {
            this.control().setValue(choice);
        }
    });

    // =========================================================================
    // CONSTRUCTOR
    // =========================================================================
    constructor() {
        this.initDealsLoader();
    }

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public load(): void {
        this.retry$.next();
    }

    // =========================================================================
    // PRIVATE METHODS
    // =========================================================================
    /**
     * Loads the salesperson's deals, and again on retry. A changed salesperson clears the picked deal at once, so it
     * can't be sent with the new salesperson while Attio loads. The first salesperson keeps it: coming back to a step
     * keeps its deal.
     */
    private initDealsLoader(): void {
        let isFirst = true;
        toObservable(this.salesPersonId)
            .pipe(
                distinctUntilChanged(),
                tap(() => {
                    if (!isFirst && this.control().value) {
                        this.control().setValue(null);
                    }
                    isFirst = false;
                }),
                switchMap(id => merge(of(undefined), this.retry$).pipe(switchMap(() => this.fetchDeals(id)))),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    private fetchDeals(salesPersonId: number | null): Observable<AttioDealOption[]> {
        this.errorMessage.set(null);
        if (salesPersonId === null) {
            this.deals.set([]);
            this.isLoading.set(false);
            return EMPTY;
        }
        this.isLoading.set(true);
        return this.attioClient.getOpenDealsForSalesPerson(salesPersonId).pipe(
            tap(deals => {
                this.deals.set(deals);
                this.isLoading.set(false);
                this.clearDealNotIn(deals);
            }),
            // A failed load keeps the picked deal: Attio being briefly unavailable shouldn't undo the user's choice.
            catchError(error => {
                const picked = this.control().value;
                this.deals.set(picked ? [picked] : []);
                this.errorMessage.set(normalizeError(error).message);
                this.isLoading.set(false);
                return EMPTY;
            }),
        );
    }

    /** By id, since a deal kept in the store is not one of the loaded objects; one still offered is left alone. */
    private clearDealNotIn(deals: AttioDealOption[]): void {
        const picked = this.control().value;
        if (picked && !deals.some(deal => deal.recordId === picked.recordId)) {
            this.control().setValue(null);
        }
    }
}
